/**
 * MongoDB Credential Form
 *
 * A form for configuring MongoDB credentials with:
 * - Connection string or individual fields
 * - Authentication options
 * - SSL/TLS settings
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

export interface MongoDBFormData {
  // Connection mode
  useConnectionString?: boolean;

  // Connection String
  connectionString: string;

  // Individual Fields
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  authSource: string;
  replicaSet: string;
  ssl: boolean;
}

interface MongoDBCredentialFormProps {
  formData: MongoDBFormData;
  onChange: (data: Partial<MongoDBFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
}

export function MongoDBCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: MongoDBCredentialFormProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const useConnectionString = formData.useConnectionString ?? !!formData.connectionString;

  const handleChange = (field: keyof MongoDBFormData, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Connection Mode */}
      <div className="space-y-3">
        <Label>Connection Method</Label>
        <RadioGroup
          value={useConnectionString ? 'string' : 'fields'}
          onValueChange={(val) => handleChange('useConnectionString', val === 'string')}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="string" id="conn-string" />
            <Label htmlFor="conn-string" className="font-normal cursor-pointer">
              Connection String
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="fields" id="conn-fields" />
            <Label htmlFor="conn-fields" className="font-normal cursor-pointer">
              Individual Fields
            </Label>
          </div>
        </RadioGroup>
      </div>

      {useConnectionString ? (
        /* Connection String Mode */
        <div className="space-y-2">
          <Label htmlFor="connectionString">
            Connection String <span className="text-red-500">*</span>
          </Label>
          <Input
            id="connectionString"
            placeholder="mongodb://localhost:27017/mydb or mongodb+srv://..."
            value={formData.connectionString}
            onChange={(e) => handleChange('connectionString', e.target.value)}
            className="font-mono text-sm"
          />
        </div>
      ) : (
        /* Individual Fields Mode */
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="host">
                Host <span className="text-red-500">*</span>
              </Label>
              <Input
                id="host"
                placeholder="localhost"
                value={formData.host}
                onChange={(e) => handleChange('host', e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="port">Port</Label>
              <Input
                id="port"
                placeholder="27017"
                value={formData.port}
                onChange={(e) => handleChange('port', e.target.value)}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="database">Database</Label>
            <Input
              id="database"
              placeholder="mydb"
              value={formData.database}
              onChange={(e) => handleChange('database', e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="admin"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                autoComplete="off"
                className="font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder={isEditing ? "Leave unchanged or enter new" : "Enter password"}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                autoComplete="new-password"
                className="font-mono text-sm"
              />
            </div>
          </div>

          {/* Advanced Settings */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <span>Advanced Settings</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="authSource">Auth Source</Label>
                  <Input
                    id="authSource"
                    placeholder="admin"
                    value={formData.authSource}
                    onChange={(e) => handleChange('authSource', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="replicaSet">Replica Set</Label>
                  <Input
                    id="replicaSet"
                    placeholder="rs0"
                    value={formData.replicaSet}
                    onChange={(e) => handleChange('replicaSet', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="ssl">Use SSL/TLS</Label>
                  <p className="text-xs text-muted-foreground">
                    Enable encrypted connection
                  </p>
                </div>
                <Switch
                  id="ssl"
                  checked={formData.ssl}
                  onCheckedChange={(checked) => handleChange('ssl', checked)}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
}
